import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Crown, Eye, EyeOff, Gift } from "lucide-react";

export default function AuthPage({ initialTab = "login" }) {
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(initialTab);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [refCode, setRefCode] = useState("");

  useEffect(() => {
    const urlRef = params.get("ref");
    const stored = localStorage.getItem("rifa_ref");
    const code = urlRef || stored || "";
    if (code) {
      setRefCode(code.toUpperCase());
      setTab("register");
    }
  }, [params]);

  const submit = async (e, mode) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        toast.success(`Bem-vindo, ${u.name}!`);
        navigate(u.role === "admin" ? "/admin" : "/dashboard");
      } else {
        const u = await register(form.name, form.email, form.password, refCode || null);
        localStorage.removeItem("rifa_ref");
        toast.success(refCode ? "Conta criada! Você foi indicado com sucesso 🎉" : "Conta criada com sucesso!");
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha na autenticação");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <span className="w-10 h-10 rounded-lg gold-btn flex items-center justify-center">
            <Crown className="w-5 h-5" />
          </span>
          <span className="heading text-2xl font-extrabold gold-gradient-text">Rifa RMC</span>
        </Link>

        <div className="card-luxury rounded-3xl p-8">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="w-full bg-slate-900/50 border border-white/5">
              <TabsTrigger value="login" data-testid="tab-login" className="flex-1 data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-200">Entrar</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register" className="flex-1 data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-200">Criar conta</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={(e) => submit(e, "login")} className="space-y-4 mt-6">
                <div>
                  <Label className="text-slate-300">E-mail</Label>
                  <Input data-testid="login-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-slate-950 border-white/10 text-slate-100" />
                </div>
                <div>
                  <Label className="text-slate-300">Senha</Label>
                  <div className="relative">
                    <Input data-testid="login-password" type={showPwd ? "text" : "password"} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 bg-slate-950 border-white/10 text-slate-100 pr-10" />
                    <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-3 top-1/2 translate-y-[-40%] text-slate-500">
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button data-testid="login-submit" type="submit" disabled={loading} className="gold-btn w-full h-11 rounded-full">
                  {loading ? "Entrando..." : "Entrar"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={(e) => submit(e, "register")} className="space-y-4 mt-6">
                {refCode && (
                  <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 flex items-center gap-2 text-sm text-emerald-200">
                    <Gift className="w-4 h-4" />
                    <span>Você foi indicado por <span className="font-mono font-bold">{refCode}</span></span>
                  </div>
                )}
                <div>
                  <Label className="text-slate-300">Nome completo</Label>
                  <Input data-testid="register-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-slate-950 border-white/10 text-slate-100" />
                </div>
                <div>
                  <Label className="text-slate-300">E-mail</Label>
                  <Input data-testid="register-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-slate-950 border-white/10 text-slate-100" />
                </div>
                <div>
                  <Label className="text-slate-300">Senha</Label>
                  <Input data-testid="register-password" type={showPwd ? "text" : "password"} required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 bg-slate-950 border-white/10 text-slate-100" />
                </div>
                <div>
                  <Label className="text-slate-300">Código de indicação (opcional)</Label>
                  <Input data-testid="register-ref" value={refCode} onChange={(e) => setRefCode(e.target.value.toUpperCase())} placeholder="Ex.: RMCABC123" className="mt-1.5 bg-slate-950 border-white/10 text-slate-100 font-mono" />
                </div>
                <Button data-testid="register-submit" type="submit" disabled={loading} className="gold-btn w-full h-11 rounded-full">
                  {loading ? "Criando..." : "Criar conta"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="my-6 flex items-center gap-3 text-slate-500 text-xs uppercase tracking-widest">
            <span className="flex-1 h-px bg-white/10" /> ou <span className="flex-1 h-px bg-white/10" />
          </div>

          <Button data-testid="google-login-btn" onClick={loginWithGoogle} variant="outline" className="w-full h-11 rounded-full border-white/15 bg-transparent text-slate-100 hover:bg-white/5">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Entrar com Google
          </Button>
        </div>
      </div>
    </div>
  );
}
